import React, { useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../styles/Agenda.css";
import { hideProcessing, showProcessing } from "../utils/utils";
import agendaService from "../services/agendaService";

// Configurar localizador do moment para português
moment.locale("pt-br");
const localizer = momentLocalizer(moment);

// Mensagens em português para o Calendar
const ptBRMessages = {
    today: "Hoje",
    previous: "Voltar",
    next: "Próximo",
    month: "Mês",
    week: "Semana",
    day: "Dia",
    agenda: "Agenda",
    date: "Data",
    time: "Hora",
    event: "Evento",
    events: "Eventos",
    noEventsInRange: "Nenhum evento neste período",
    showMore: (total) => `+${total} mais`
};

export default function Agenda({ agendamentos = [], removerBloqueio = false }) {
    const toastRef = React.useRef(null);
    const [selectedAgenda, setSelectedAgenda] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [view, setView] = useState(Views.MONTH);
    const [date, setDate] = useState(new Date());

    // Mapeia status para cores do big-calendar
    const getStatusColor = (status) => {
        const colorMap = {
            "AGENDADO": "#FFC107",
            "EM PROGRESSO": "#FF9800",
            "CONCLUÍDO": "#4CAF50",
            "CANCELADO": "#F44336",
            "BLOQUEADO": "#a5a5a5"
        };
        return colorMap[status] || "#9C27B0";
    };

    // Mapeia agendamentos para eventos do big-calendar
    const events = useMemo(() => {
        return agendamentos.map((agenda) => {
            const startDate = new Date(agenda.dataCompromisso);

            var endDate;
            if (agenda.finalCompromisso ) {
                endDate = new Date(agenda.finalCompromisso);
            } else {
                endDate = new Date(agenda.dataCompromisso);
                endDate.setHours(endDate.getHours() + 1); // Duração de 1 hora por padrão
            }
            

            return {
                id: agenda.id || Math.random(),
                title: agenda.equipe?.nome || "Sem equipe",
                start: startDate,
                end: endDate,
                resource: agenda,
                backgroundColor: getStatusColor(agenda.status),
                borderColor: getStatusColor(agenda.status)
            };
        });
    }, [agendamentos]);

    // Retorna estilos de status para o dialog
    const getStatusSeverity = (status) => {
        const statusMap = {
            "AGENDADO": "warning",
            "CONCLUÍDO": "success",
            "CANCELADO": "danger",
            "EM PROGRESSO": "primary",
            "BLOQUEADO": "secondary"
        };
        return statusMap[status] || "secondary";
    };

    // Handler para seleção de evento
    const handleSelectEvent = async (event) => {

        showProcessing();

        try{
            const response = await agendaService.getByExternalUuid(event.resource.externalAccessUuid);

            if (response && response.data) {
                setSelectedAgenda(response.data);
                setShowDetails(true);
            }

        }catch(e){
            console.error("Erro ao buscar detalhes do agendamento:", e);
        }
        hideProcessing();
        
    };

    // Verifica se bloqueio está ativo (data final maior que hoje)
    const isBloqueioAtivo = (agenda) => {
        if (agenda.status !== "BLOQUEADO" || !agenda.finalCompromisso) {
            return false;
        }
        const hoje = new Date();
        const dataFinal = new Date(agenda.finalCompromisso);
        return dataFinal > hoje;
    };

    // Handler para desbloquear agenda
    const handleDesbloquearAgenda = async () => {

        if (window.confirm("Deseja realmente desbloquear esta agenda?")) {
            confirmarDesbloqueio();
        }
    };

    const confirmarDesbloqueio = async () => {
        showProcessing();
        toastRef.current.clear();

        try {
            await agendaService.delete(selectedAgenda.id);
            
            setShowDetails(false);
            setSelectedAgenda(null);

            toastRef.current?.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Agenda desbloqueada com sucesso',
                life: 3000
            });

            // Recarregar agendas se necessário (pai deve fazer isso)
            window.location.reload();

        } catch (error) {
            console.error("Erro ao desbloquear agenda:", error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data || 'Erro ao desbloquear agenda',
                life: 3000
            });
        } finally {
            hideProcessing();
        }
    };

    // Customização de estilo dos eventos
    const eventStyleGetter = (event) => {
        const style = {
            backgroundColor: event.backgroundColor,
            borderRadius: "5px",
            opacity: 0.9,
            color: "white",
            border: `2px solid ${event.borderColor}`,
            display: "block",
            cursor: "pointer"
        };

        return {
            style
        };
    };

    // Formata data e hora
    const formatarDataHora = (data) => {
        if (!data) return "N/A";
        const date = new Date(data);
        return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        }).replace(",", " às");
    };

    // Dialog com detalhes do agendamento
    const renderAgendaDetails = () => {
        if (!selectedAgenda) return null;

        return (
            <Dialog
                visible={showDetails}
                onHide={() => setShowDetails(false)}
                header="Detalhes do Agendamento"
                modal
                style={{ width: "90vw", maxWidth: "900px" }}
                maximizable
            >
                <div className="agenda-details">
                    {/* Link de Acesso Externo */}
                    {selectedAgenda.externalAccessUuid && selectedAgenda.status !== "BLOQUEADO" && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    🔗 Acesso Externo
                                </h3>
                                <div style={{
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center",
                                    backgroundColor: "#f0f7ff",
                                    padding: "12px",
                                    borderRadius: "4px",
                                    borderLeft: "4px solid #2196F3"
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <p style={{
                                            fontSize: "12px",
                                            color: "#666",
                                            margin: "0 0 5px 0"
                                        }}>
                                            Link para acesso externo:
                                        </p>
                                        <a 
                                            href={`/agendamento/${selectedAgenda.externalAccessUuid}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: "#2196F3",
                                                textDecoration: "none",
                                                wordBreak: "break-all",
                                                fontSize: "12px"
                                            }}
                                        >
                                            {`${window.location.origin}/agendamento-externo/${selectedAgenda.externalAccessUuid}`}
                                        </a>
                                    </div>
                                    <Button
                                        icon="pi pi-external-link"
                                        className="p-button-rounded p-button-outlined p-button-info"
                                        onClick={() => window.open(`/agendamento-externo/${selectedAgenda.externalAccessUuid}`, '_blank')}
                                        title="Abrir em nova aba"
                                        style={{ minWidth: "36px", minHeight: "36px" }}
                                    />
                                    <Button
                                        icon="pi pi-copy"
                                        className="p-button-rounded p-button-outlined"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}/agendamento-externo/${selectedAgenda.externalAccessUuid}`);
                                        }}
                                        title="Copiar link"
                                        style={{ minWidth: "36px", minHeight: "36px" }}
                                    />
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Header com Status e ID */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px"
                    }}>
                        <div>
                            <h3 style={{ margin: "0 0 5px 0", color: "#333", fontSize: "18px" }}>
                                ID: {selectedAgenda.id}
                            </h3>
                        </div>
                        <Tag value={selectedAgenda.status} severity={getStatusSeverity(selectedAgenda.status)} />
                    </div>

                    <Divider />

                    {/* Tipo e Data */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "20px",
                        marginBottom: "20px"
                    }}>
                        <div>
                            <h4 style={{ color: "#333", marginBottom: "8px", fontSize: "12px", fontWeight: "600" }}>
                                Tipo
                            </h4>
                            <p style={{ fontSize: "14px", color: "#555", margin: 0 }}>
                                {selectedAgenda.tipo || "N/A"}
                            </p>
                        </div>
                        <div>
                            <h4 style={{ color: "#333", marginBottom: "8px", fontSize: "12px", fontWeight: "600" }}>
                                📅 Data e Hora
                            </h4>
                            <p style={{ fontSize: "14px", color: "#555", margin: 0 }}>
                                {formatarDataHora(selectedAgenda.dataCompromisso)}

                                {selectedAgenda.status === "BLOQUEADO" && selectedAgenda.finalCompromisso && (
                                    <> até {formatarDataHora(selectedAgenda.finalCompromisso)}</>
                                )}
                            </p>
                        </div>
                    </div>`

                    <Divider />

                    {/* Informações do Cliente */}
                    {selectedAgenda.cliente && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "15px", fontSize: "13px", fontWeight: "600" }}>
                                    👤 Cliente
                                </h3>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "15px"
                                }}>
                                    <div style={{
                                        backgroundColor: "#f9f9f9",
                                        padding: "12px",
                                        borderRadius: "4px"
                                    }}>
                                        <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                            Nome
                                        </p>
                                        <p style={{ fontSize: "14px", color: "#333", margin: 0, fontWeight: "500" }}>
                                            {selectedAgenda.cliente.nome}
                                        </p>
                                    </div>

                                    {selectedAgenda.cliente.telefoneCelular && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                                📱 Celular
                                            </p>
                                            <p style={{ fontSize: "14px", color: "#333", margin: 0 }}>
                                                {selectedAgenda.cliente.telefoneCelular}
                                            </p>
                                        </div>
                                    )}

                                    {selectedAgenda.cliente.telefoneComercial && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                                ☎️ Comercial
                                            </p>
                                            <p style={{ fontSize: "14px", color: "#333", margin: 0 }}>
                                                {selectedAgenda.cliente.telefoneComercial}
                                            </p>
                                        </div>
                                    )}

                                    {selectedAgenda.tipoTelhado && (
                                        <div style={{
                                            backgroundColor: "#f9f9f9",
                                            padding: "12px",
                                            borderRadius: "4px"
                                        }}>
                                            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#666", fontWeight: "600" }}>
                                                🏠 Tipo de Telhado
                                            </p>
                                            <p style={{ fontSize: "14px", color: "#333", margin: 0 }}>
                                                {selectedAgenda.tipoTelhado}
                                            </p>
                                        </div>
                                    )}
                                
                                    
                                </div>

                                {/* Endereços */}
                                {selectedAgenda.cliente.enderecos && selectedAgenda.cliente.enderecos.length > 0 && (
                                    <div style={{ marginTop: "15px" }}>
                                        <h4 style={{ color: "#333", marginBottom: "10px", fontSize: "12px", fontWeight: "600" }}>
                                            📍 Endereço(s)
                                        </h4>
                                        {selectedAgenda.cliente.enderecos.map((endereco, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    backgroundColor: "#f9f9f9",
                                                    padding: "12px",
                                                    borderRadius: "4px",
                                                    marginBottom: "10px",
                                                    borderLeft: "4px solid #667eea"
                                                }}
                                            >
                                                <p style={{
                                                    margin: "0 0 8px 0",
                                                    fontSize: "13px",
                                                    fontWeight: "600",
                                                    color: "#333"
                                                }}>
                                                    {endereco.logradouro}, {endereco.numero}
                                                    {endereco.complemento && ` - ${endereco.complemento}`}
                                                </p>
                                                <p style={{
                                                    margin: "5px 0",
                                                    fontSize: "12px",
                                                    color: "#666"
                                                }}>
                                                    {endereco.bairro} - {endereco.cidade}, {endereco.estado}
                                                </p>
                                                {endereco.cep && (
                                                    <p style={{
                                                        margin: "5px 0 0 0",
                                                        fontSize: "12px",
                                                        color: "#999"
                                                    }}>
                                                        CEP: {endereco.cep}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Ordem de Serviço */}
                    {selectedAgenda.ordem && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    📋 Ordem de Serviço
                                </h3>
                                <div style={{ padding: "12px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                                    <p style={{ fontSize: "14px", color: "#555", margin: 0 }}>
                                        <strong>ID:</strong> #{selectedAgenda.ordem.id}
                                    </p>
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Valor */}
                    {selectedAgenda.ordem?.valor && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    💰 Valor da Ordem
                                </h3>
                                <p style={{
                                    fontSize: "18px",
                                    color: "#249152",
                                    margin: 0,
                                    fontWeight: "600"
                                }}>
                                    {new Intl.NumberFormat("pt-BR", {
                                        style: "currency",
                                        currency: "BRL"
                                    }).format(selectedAgenda.ordem.valor)}
                                </p>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Observações */}
                    {selectedAgenda.observacoes && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    📝 Observações
                                </h3>
                                <p style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    margin: 0,
                                    lineHeight: "1.6",
                                    whiteSpace: "pre-wrap",
                                    backgroundColor: "#f9f9f9",
                                    padding: "12px",
                                    borderRadius: "4px"
                                }}>
                                    {selectedAgenda.observacoes}
                                </p>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Informações de Monitoramento */}
                    {selectedAgenda.monitoramentoAtivo && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    🔐 Informações de Monitoramento
                                </h3>
                                <div style={{
                                    backgroundColor: "#f5f5f5",
                                    padding: "15px",
                                    borderRadius: "4px"
                                }}>
                                    {selectedAgenda.usuarioMonitoramento && (
                                        <p style={{ fontSize: "12px", color: "#555", margin: "5px 0" }}>
                                            <strong>Usuário:</strong> {selectedAgenda.usuarioMonitoramento}
                                        </p>
                                    )}
                                    {selectedAgenda.senhaMonitoramento && (
                                        <p style={{ fontSize: "12px", color: "#555", margin: "5px 0" }}>
                                            <strong>Senha:</strong> {selectedAgenda.senhaMonitoramento}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Observações do Técnico */}
                    {selectedAgenda.observacoesTecnico && (
                        <>
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "13px", fontWeight: "600" }}>
                                    👨‍🔧 Observações do Técnico
                                </h3>
                                <p style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    margin: 0,
                                    lineHeight: "1.6",
                                    whiteSpace: "pre-wrap",
                                    backgroundColor: "#f9f9f9",
                                    padding: "12px",
                                    borderRadius: "4px"
                                }}>
                                    {selectedAgenda.observacoesTecnico}
                                </p>
                            </div>
                            <Divider />
                        </>
                    )}

                    {/* Datas de criação e modificação */}
                    <div style={{
                        fontSize: "11px",
                        color: "#999",
                        textAlign: "center",
                        marginBottom: "20px"
                    }}>
                        <p style={{ margin: "5px 0" }}>
                            Criado em: {formatarDataHora(selectedAgenda.criadoEm)}
                        </p>
                        {selectedAgenda.alteradoEm && (
                            <p style={{ margin: "5px 0" }}>
                                Última atualização: {formatarDataHora(selectedAgenda.alteradoEm)}
                            </p>
                        )}
                    </div>

                    {/* Botão Desbloquear - Aparece apenas para bloqueios ativos quando removerBloqueio é true */}
                    {removerBloqueio && isBloqueioAtivo(selectedAgenda) && (
                        <>
                            <Divider />
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "10px",
                                marginTop: "20px"
                            }}>
                                <Button
                                    label="Desbloquear Agenda"
                                    icon="pi pi-unlock"
                                    className="p-button-danger"
                                    onClick={handleDesbloquearAgenda}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        );
    };

    return (
        <div className="agenda-container">
            <Toast ref={toastRef} position="top-right" />
            <div className="agenda-card">
                {/* Legenda de Status */}
                <div style={{
                    display: "flex",
                    gap: "20px",
                    flexWrap: "wrap",
                    marginBottom: "20px",
                    padding: "15px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "4px",
                    borderLeft: "4px solid #333"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#FFC107",
                            borderRadius: "3px"
                        }} />
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>AGENDADO</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#FF9800",
                            borderRadius: "3px"
                        }} />
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Em Progresso</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#4CAF50",
                            borderRadius: "3px"
                        }} />
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Concluído</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#F44336",
                            borderRadius: "3px"
                        }} />
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Cancelado</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#a5a5a5",
                            borderRadius: "3px"
                        }} />
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Bloqueado</span>
                    </div>
                </div>

                <div className="agenda-content big-calendar-wrapper">
                    
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        locale="pt-BR"
                        endAccessor="end"
                        style={{ height: "600px" }}
                        view={view}
                        onView={setView}
                        date={date}
                        onNavigate={setDate}
                        views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        popup
                        selectable
                        messages={ptBRMessages}
                    />
                    
                </div>
            </div>

            {renderAgendaDetails()}
        </div>
    );
}
